import helpers from '@/lib/helpers'

/**
 * A phone number reduced to digits.
 *
 * Pasted numbers arrive in every format going - (209) 555-1234, 209.555.1234,
 * "Tel: 209 555 1234" - and storing them as written makes the same number look
 * like several. The server keeps its own digits-only contact_clean for
 * matching, but the value on the record is what people read, so it is
 * normalised here too.
 *
 * Two things are NOT simply stripped, because doing so would turn a real
 * number into a wrong one rather than a tidier one:
 *
 *  - a leading "+" is kept. It is not formatting, it is what makes +44 20...
 *    a UK number instead of a malformed US one.
 *  - an extension is dropped rather than run onto the end. "555-1234 x23"
 *    stripped naively becomes 555123423, which is not a phone number at all.
 */
export function cleanPhone(value) {
  const raw = String(value || '').trim()

  // cut a trailing extension: x23, ext 23, ext. 23, extension 23, #23
  const base = raw.split(/\s*(?:x|ext\.?|extension|#)\s*\d+\s*$/i)[0].trim()
  const digits = base.replace(/\D/g, '')

  return base.startsWith('+') ? '+' + digits : digits
}

/**
 * Write the emails and phones out of a pasted block as contact rows.
 *
 * Shared by the address dialog and the contact dialog, because a pasted
 * signature lands in whichever one the user happened to open and should do the
 * same thing either way.
 *
 * Posted one at a time rather than as a batch: customers_contact rejects a
 * duplicate or a malformed number per row, and one bad line should not cost
 * the others. Failures are collected and reported together.
 *
 * @param {string} url      the record's contact endpoint
 * @param {object} parsed   the dialog's result
 * @param {object} targets  which groups the user ticked, e.g. {emails: true}
 * @returns {Promise<number>} how many were added
 */
export async function saveContacts(url, parsed, targets = {}) {
  const wanted = [
    ...(targets.emails ? (parsed.emails || []).map(contact => ({contact, method: 'E'})) : []),
    ...(targets.phones
        ? (parsed.phones || []).map(v => ({contact: cleanPhone(v), method: 'P'})).filter(r => r.contact)
        : []),
  ]

  if (!wanted.length)
    return 0

  let added = 0
  const failed = []

  for (const row of wanted) {
    try {
      // the global spinner rides across the whole loop: activity_done defers
      // its last decrement by 750ms and the next request cancels that timer,
      // so sequential posts hold it steady instead of flickering
      await helpers.asyncAction(url, 'POST', {
        quiet: true,
        data: {...row, name: parsed.name || ''},
      })
      added++
    } catch (e) {
      failed.push(`${row.contact} (${(e && e.message) || 'rejected'})`)
    }
  }

  if (added)
    helpers.toast(`Added ${added} contact${added === 1 ? '' : 's'}`)

  if (failed.length)
    helpers.alert('Not added:\n' + failed.join('\n'))

  return added
}

/**
 * Write the postal address out of a pasted block as an address record.
 *
 * Quiet, and the failure is reported here rather than thrown, so a rejected
 * address does not take the contacts that saved alongside it with it.
 *
 * @returns {Promise<boolean>} whether it saved
 */
export async function saveAddress(url, parsed) {
  try {
    await helpers.asyncAction(url, 'POST', {
      quiet: true,
      data: {
        name: parsed.name || '',
        company: parsed.company || '',
        address1: parsed.address1 || '',
        address2: parsed.address2 || '',
        city: parsed.city || '',
        state: parsed.state || '',
        zip: parsed.zip || '',
        country: parsed.country || '',
        residential: !!parsed.residential,
        notes: parsed.notes || '',
      },
    })
    helpers.toast('Address saved')
    return true
  } catch (e) {
    helpers.alert('Address not saved: ' + ((e && e.message) || 'rejected'))
    return false
  }
}

export default {saveContacts, saveAddress, cleanPhone}
